import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { enrich, deductionMetrics, netBalanceAt } from "@/lib/finance";
import { Header } from "@/components/vault/Header";
import { KpiCards } from "@/components/vault/KpiCards";
import { TrancheModal } from "@/components/vault/TrancheModal";
import { TrancheList } from "@/components/vault/TrancheList";
import { GrowthChart } from "@/components/vault/GrowthChart";
import { ProjectionTool } from "@/components/vault/ProjectionTool";
import { PayoutHub } from "@/components/vault/PayoutHub";
import { DeductionsPanel } from "@/components/vault/DeductionsPanel";
import { LedgerTable } from "@/components/vault/LedgerTable";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Dashboard() {
  const [rawTranches, setRawTranches] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [nowUtc, setNowUtc] = useState("");

  const loadAll = useCallback(async () => {
    const [tr, po, de, lg] = await Promise.all([
      api.listTranches(), api.listPayouts(), api.listDeductions(), api.ledger(),
    ]);
    setRawTranches(tr); setPayouts(po); setDeductions(de); setLedger(lg);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
      setNowUtc(new Date().toISOString().slice(11, 19));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const tranches = useMemo(() => rawTranches.map(enrich), [rawTranches, tick]);

  const settledTotal = useMemo(
    () => payouts.filter((p) => p.status === "SETTLED").reduce((s, p) => s + p.requested_amount, 0),
    [payouts]
  );

  const live = useMemo(() => {
    const total_active_capital = tranches.reduce((s, t) => s + t.principal_amount, 0);
    const grossToday = tranches.reduce((s, t) => s + t.todays_profit, 0);
    const dm = deductionMetrics(deductions);
    const net_balance = netBalanceAt(tranches, deductions, 0);
    return {
      total_active_capital,
      total_current_balance: net_balance,
      total_accrued_yield: net_balance + dm.raw - total_active_capital,
      todays_profit: grossToday - dm.todayImpact,
      total_deducted: dm.raw,
      total_liquidated_payouts: settledTotal,
      active_tranche_count: tranches.length,
    };
  }, [tranches, deductions, settledTotal]);

  const createTranche = async (body) => { await api.createTranche(body); await loadAll(); };
  const deleteTranche = async (id) => { await api.deleteTranche(id); await loadAll(); toast.success("Tranche removed"); };
  const createPayout = async (body) => { await api.createPayout(body); await loadAll(); };
  const advancePayout = async (id, status) => { await api.updatePayout(id, status); await loadAll(); toast.success(`Moved to ${status}`); };
  const deletePayout = async (id) => { await api.deletePayout(id); await loadAll(); };
  const createDeduction = async (body) => { await api.createDeduction(body); await loadAll(); };
  const deleteDeduction = async (id) => { await api.deleteDeduction(id); await loadAll(); toast.success("Deduction removed"); };
  const seed = async () => { await api.seed(); await loadAll(); toast.success("Demo tranches loaded"); };

  return (
    <div className="min-h-screen aureon-grid-bg">
      <Header onInject={() => setModalOpen(true)} nowUtc={nowUtc} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <KpiCards live={live} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GrowthChart tranches={tranches} deductions={deductions} />
          <ProjectionTool tranches={tranches} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <TrancheList tranches={tranches} onDelete={deleteTranche} />
            {tranches.length === 0 && (
              <div className="text-center">
                <Button data-testid="seed-btn" variant="outline" onClick={seed}
                  className="border-[#232D40] bg-white/5 text-slate-300">
                  Load demo tranches
                </Button>
              </div>
            )}
            <DeductionsPanel
              deductions={deductions} totalDrawn={live.total_deducted}
              onCreate={createDeduction} onDelete={deleteDeduction}
            />
          </div>
          <div className="lg:col-span-2">
            <PayoutHub payouts={payouts} onCreate={createPayout} onAdvance={advancePayout} onDelete={deletePayout} />
          </div>
        </div>

        <LedgerTable entries={ledger} />

        <footer className="py-6 text-center text-xs text-slate-600 font-mono-data">
          Aureon Vault · Private tracking ledger · Net of deductions · A = P × (1.05)ᵗ from each injection timestamp
        </footer>
      </main>

      <TrancheModal open={modalOpen} onOpenChange={setModalOpen} onCreate={createTranche} />
    </div>
  );
}
